import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Package } from 'lucide-react';

const AddProductModal = ({ isOpen, onClose, onAdd, availableProducts }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    producto_id: '',
    cantidad_planificada: ''
  });

  useEffect(() => {
    if (isOpen) {
      setFormData({
        producto_id: '',
        cantidad_planificada: ''
      });
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const producto_id = parseInt(formData.producto_id);
    const cantidad_planificada = parseInt(formData.cantidad_planificada);
    
    if (!producto_id || isNaN(cantidad_planificada) || cantidad_planificada < 0) {
      return;
    }

    onAdd(producto_id, cantidad_planificada);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('planProduction.addProductTitle')}</h2>
          <button className="modal-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          <div className="form-group">
            <label htmlFor="producto_id">
              <Package size={18} />
              {t('planProduction.table.product')}
            </label>
            <select
              id="producto_id"
              name="producto_id"
              value={formData.producto_id}
              onChange={handleChange}
              required
              className="form-select"
            >
              <option value="">{t('planProduction.selectProductPlaceholder')}</option>
              {availableProducts.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.nombre} ({product.code})
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="cantidad_planificada">
              <Package size={18} />
              {t('planProduction.table.planned')}
            </label>
            <input
              type="number"
              id="cantidad_planificada"
              name="cantidad_planificada"
              value={formData.cantidad_planificada}
              onChange={handleChange}
              placeholder={t('planProduction.enterPlannedQuantity')}
              required
              min="0"
              className="form-input"
            />
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-outline"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
            >
              {t('common.add')}
            </button>
          </div>
        </form>

        <style jsx>{`
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
            padding: var(--spacing-4);
          }

          .modal-content {
            background: white;
            border-radius: var(--radius-lg);
            max-width: 600px;
            width: 100%;
            max-height: 90vh;
            overflow-y: auto;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
                        0 10px 10px -5px rgba(0, 0, 0, 0.04);
          }

          .modal-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: var(--spacing-6);
            border-bottom: 1px solid var(--gray-200);
            background: linear-gradient(135deg, #2c5f2d 0%, #1a4d2e 100%);
            color: white;
            border-radius: var(--radius-lg) var(--radius-lg) 0 0;
          }

          .modal-header h2 {
            font-size: var(--font-size-2xl);
            font-weight: 700;
            color: white;
            margin: 0;
          }

          .modal-close {
            background: rgba(255, 255, 255, 0.1);
            border: none;
            color: white;
            cursor: pointer;
            padding: var(--spacing-2);
            border-radius: var(--radius-md);
            transition: all 0.2s;
          }

          .modal-close:hover {
            background-color: rgba(255, 255, 255, 0.2);
            color: white;
          }

          .modal-body {
            padding: var(--spacing-6);
          }

          .form-group {
            margin-bottom: var(--spacing-4);
          }

          .form-group label {
            display: flex;
            align-items: center;
            gap: var(--spacing-2);
            font-weight: 600;
            color: var(--gray-700);
            margin-bottom: var(--spacing-2);
            font-size: var(--font-size-sm);
          }

          .form-input,
          .form-select {
            width: 100%;
            padding: var(--spacing-3);
            border: 2px solid var(--gray-300);
            border-radius: var(--radius-md);
            font-size: var(--font-size-base);
            transition: border-color 0.2s;
          }

          .form-input:focus,
          .form-select:focus {
            outline: none;
            border-color: var(--primary-blue);
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
          }

          .modal-footer {
            display: flex;
            justify-content: flex-end;
            gap: var(--spacing-3);
            padding-top: var(--spacing-4);
            border-top: 1px solid var(--gray-200);
            margin-top: var(--spacing-4);
          }

          @media (max-width: 768px) {
            .modal-content {
              max-width: 100%;
              margin: var(--spacing-4);
            }

            .modal-header,
            .modal-body {
              padding: var(--spacing-4);
            }

            .modal-footer {
              flex-direction: column;
            }

            .modal-footer button {
              width: 100%;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default AddProductModal;
